import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { hLeftAlignStyle } from '@components/model-structure/lookup-setup/helpers/constants';
import { resultsSpreadConfig } from '@components/results/sheet-builders/results-spread-config';
import * as GC from '@grapecity/spread-sheets';
import { copyValues } from '@helpers/spread/sheet-functions';
import { ImportError } from '@models/import-settings';
import { WindowRef } from '@progress/kendo-angular-dialog';
import { MenuService } from '@services/menu/menu.service';
import { setDefaultBookOptions, setDefaultSheetOptions } from '@shared/spreadsheet/base';
import { combineLatest, Subscription } from 'rxjs';
import { startWith } from 'rxjs/operators';

@Component({
  selector: 'app-import-error-display',
  templateUrl: './import-error-display.component.html',
  styleUrls: ['./import-error-display.component.scss'],
})
export class ImportErrorDisplayComponent implements OnInit, OnChanges, OnDestroy {
  public spreadBackColor = resultsSpreadConfig.backColor; // Fix 3532 Part 3
  public hostStyle = {
    width: '100%',
    height: '100%',
  };

  @Input() errorList: ImportError[] = null;
  @Input() filterBy: string = null;

  private spread: GC.Spread.Sheets.Workbook = null;
  private sheet: GC.Spread.Sheets.Worksheet = null;
  private filter: GC.Spread.Sheets.Filter.HideRowFilter;
  private subscriptions = new Subscription();

  constructor(private readonly menuService: MenuService, public window: WindowRef) {}

  ngOnInit() {
    const winInstance = this.window.window.instance;

    // Emit resize event for any changes to the window
    const resizeListeners$ = combineLatest([
      winInstance.resizeEnd.pipe(startWith(null)),
      winInstance.heightChange.pipe(startWith(null)),
      winInstance.stateChange.pipe(startWith(null)),
    ]).subscribe(status => {
      this.menuService.emitResize();
      winInstance.focus();
    });
    this.subscriptions.add(resizeListeners$);
  }

  ngOnChanges() {
    this.setPageColumns(this.mapErrors(this.errorList));
    if (this.filterBy) {
      this.setFilter(this.filterBy);
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  public workbookInit(args) {
    this.spread = args.spread;
    this.setupWorkBook();
  }

  private setupWorkBook() {
    this.menuService.emitResize();

    this.sheet = this.spread.getActiveSheet();
    this.sheet.suspendPaint();

    // =========================
    //   General Sheet Options
    // =========================

    this.sheet.name('Error List');
    this.sheet.setColumnCount(1);
    this.sheet.defaults.rowHeight = 20;
    this.sheet.defaults.colWidth = 100;

    // Book and Sheet Options
    setDefaultBookOptions(this.spread.options);
    setDefaultSheetOptions(this.sheet.options);

    this.sheet.options.isProtected = true;
    this.sheet.options.protectionOptions.allowFilter = true;
    this.sheet.options.protectionOptions.allowSort = true;
    this.sheet.options.protectionOptions.allowSelectLockedCells = true;
    this.spread.options.allowCopyPasteExcelStyle = true;

    const filteredMenuOptions = this.spread.contextMenu.menuData.filter(item => ['gc.spread.copy', 'gc.spread.filter'].includes(item.name));
    this.spread.contextMenu.menuData = [...filteredMenuOptions];
    // Setup databinding
    this.setPageColumns(this.mapErrors(this.errorList));

    const commandManager = this.spread.commandManager();
    commandManager.register('copy', (name, command, isUndo, x) => copyValues(name, command, isUndo));
    commandManager.setShortcutKey('copy', GC.Spread.Commands.Key.c, true, false, false);

    this.sheet.resumePaint();
  }

  private setPageColumns(data) {
    if (this.sheet) {
      this.sheet.autoGenerateColumns = true;
      this.sheet.setDataSource(data);

      this.sheet.bindColumns([
        { name: 'dataset', displayName: 'Dataset' },
        { name: 'ImportStep', displayName: 'Import Step' },
        { name: 'error_type', displayName: 'Error Type' },
        { name: 'error_text', displayName: 'Error Text', size: 1024 }, // ERROR_TEXT_COLUMN
      ]);
      const ERROR_TEXT_COLUMN = 3;

      this.sheet.setStyle(0, ERROR_TEXT_COLUMN, hLeftAlignStyle, GC.Spread.Sheets.SheetArea.colHeader);

      this.filter = new GC.Spread.Sheets.Filter.HideRowFilter(
        new GC.Spread.Sheets.Range(0, 0, this.sheet.getRowCount(), this.sheet.getColumnCount())
      ).filterDialogVisibleInfo({
        sortByValue: true,
        sortByColor: false,
        filterByColor: false,
        filterByValue: true,
        listFilterArea: true,
      });
      this.sheet.rowFilter(this.filter);
      this.spread.options.autoFitType = GC.Spread.Sheets.AutoFitType.cellWithHeader;
      [0, 1, 2].forEach(i => this.sheet.autoFitColumn(i));
      this.setFilter(this.filterBy);
    }
  }

  setupHeaders = (sheet: GC.Spread.Sheets.Worksheet) => {
    sheet.setColumnCount(4, GC.Spread.Sheets.SheetArea.colHeader);
    sheet.setValue(0, 0, 'Dataset', GC.Spread.Sheets.SheetArea.colHeader);
    sheet.setValue(0, 1, 'Import Step', GC.Spread.Sheets.SheetArea.colHeader);
    sheet.setValue(0, 2, 'Error Type', GC.Spread.Sheets.SheetArea.colHeader);
    sheet.setValue(0, 3, 'Error Text', GC.Spread.Sheets.SheetArea.colHeader);
  };

  setFilter = datasetName => {
    if (this.filter && datasetName) {
      this.filter.unfilter(0);
      var condition = new GC.Spread.Sheets.ConditionalFormatting.Condition(
        GC.Spread.Sheets.ConditionalFormatting.ConditionType.textCondition,
        {
          compareType: GC.Spread.Sheets.ConditionalFormatting.TextCompareType.equalsTo,
          expected: datasetName,
        }
      );
      condition.ignoreCase();
      this.filter.addFilterItem(0, condition);
      this.filter.filter(0);
    }
  };

  public mapErrors(errorList: ImportError[]): ImportError[] {
    // Perform data manipulation if needed here
    return errorList;
  }

  public onOkClicked(event) {
    this.window.close();
  }
}
